import React, { useState, useEffect, useMemo } from "react";
import {
    Typography,
    Grid,
    TablePagination,
    Box,
    CircularProgress,
    Tooltip,
    Table,
    TableHead,
    TableBody,
    TableRow,
    TableCell,
    Divider,
} from "@material-ui/core";
import { Link } from "react-router-dom";
import { Alert } from "@material-ui/lab";
import UpdateIcon from "@mui/icons-material/Update";
import moment from "moment-timezone";
import "moment/locale/es";
import { IconService } from "./IconService";
import { Step } from "./Step";
import { PopoverFlashboard } from "./PopoverFlashboard";
import {
    getImagenAtentus,
    playSound,
    sortObjetivosByCustomColors,
} from "../utils/fns";
import { api_auth, api_host } from "../../../common/globals/constants";
import { useInterval } from "../../../hooks/useInterval";
import Skeleton from "./Skeleton";
import FilterInputs from "./FilterInputs";
import queryString from "query-string";
import { differenceInSeconds, format } from "date-fns";
import TimerIcon from "@mui/icons-material/Timer";
import ChipTag, { isColorLight } from "../../../components/TagsAtentus/ChipTag";
import { ChipVerMas } from "../../ManageObjective/components/ListObjectives";
import { getQueryParams } from "../../../utils/fns/get-query-params";
import { useConfiguracionLimites } from "../../ManageObjective/ThresholdConfig/hooks/useConfiguracionLimites";
import CloudOffIcon from "@mui/icons-material/CloudOff";
import CircleIcon from "@mui/icons-material/Circle";
import { useTranslation } from "react-i18next";
moment.locale("es");

/**
 * 
 * @returns {Object} - Parsea parámetros de URL incluyendo tags, filter type, state, auto pagination, y search.
 * @description Parsea los parámetros de la URL para extraer el estado inicial de los inputs de filtro.
 */
function getUrlParamsState() {
    const queryParamsParsed = queryString.parse(document.location.search);
    const urlTags = queryParamsParsed["etiquetas"];
    const urlTagsList = urlTags
        ? urlTags.split("T").map((i) => parseInt(i))
        : [];
    const urlFilterType = queryParamsParsed["filterType"] ?? "OR";
    const urlEstado = queryParamsParsed["estado"] ?? "Todos";
    const autoPagination = queryParamsParsed["autoPagination"] === "true";
    const urlSearch = queryParamsParsed["search"] ?? "";

    const qToken = queryParamsParsed["token"];

    return {
        urlTagsList,
        urlFilterType,
        urlEstado,
        autoPagination,
        qToken,
        urlSearch,
    };
}

const URL_PARAMS_STATE = getUrlParamsState();

export default function ContentDisponibilidadUmbralPlus() {
    const [objetivos, setObjetivos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dateUpdate, setDateUpdate] = useState(
        moment().locale("es").format("DD/MM/YYYY, HH:mm:ss")
    );
    const [alerta, setAlerta] = useState(false);
    const [imagen, setImagen] = useState("");
    const [titulo, setTitutlo] = useState("");
    const [searchTerm, setSearchTerm] = useState(URL_PARAMS_STATE.urlSearch);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(4);
    const [token] = React.useState(
        sessionStorage.getItem("key") || URL_PARAMS_STATE.qToken
    );
    const [user] = React.useState(sessionStorage.getItem("user"));
    const [tags, setTags] = useState(URL_PARAMS_STATE.urlTagsList);
    const [filterTagsType, setFilterTagsType] = useState(
        URL_PARAMS_STATE.urlFilterType
    );
    const [autoPlaySlider, setAutoPlaySlider] = useState(
        URL_PARAMS_STATE.autoPagination
    );
    const [intervalTime, setIntervalTime] = useState(null);
    const [stateFilter, setStateFilter] = useState(
        URL_PARAMS_STATE.urlEstado
    );
    const [isError, setIsError] = useState(false);
    const [isFirstRender, setIsFirstRender] = useState(true);
    const {
        data: dataColores,
        isSuccess,
        isError: isErrorConfiguracionLimites,
    } = useConfiguracionLimites();
    const [fetchIntervalTime, setFetchIntervalTime] = useState(null);
    const [pathname] = useState(document.location.pathname);
    const { t } = useTranslation();

    useInterval(() => {
        fetchData();
    }, fetchIntervalTime);

    useEffect(() => {
        if (isSuccess && Boolean(token)) {
            fetchData();
            setFetchIntervalTime(300000);
        }
    }, [isSuccess]);

    const fetchData = () => {
        setLoading(true);
        setIsError(false);
        const dateMilisegundos = Date.now();
        const isPublic = document.location.pathname === "/public/flashboard";
        const urlToken = getUrlParamsState().qToken;
        const publicUrl = `${api_host}/public/flashboard/getFlashboardPlusInfo/?token=${urlToken}&key=${api_auth}`;
        const withAuthUrl = `${api_host}/reporte/online/indicadores/getFlashboardPlusInfo/?token=${token}&user=${user}&key=${api_auth}&start=${dateMilisegundos}`;

        fetch(isPublic ? publicUrl : withAuthUrl)
            .then((response) => response.json())
            .then((data) => {
                const colores = Object.assign({}, [
                    "#C2272D",
                    "#da8e1d",
                    "#FFC600",
                    ...dataColores.map((item) => item.color),
                    "#41B900",
                    "#bdbdbd",
                ]);
                const sortedObjetivos = sortObjetivosByCustomColors(
                    data.objetivos,
                    colores
                );
                const objetivosConEstado = sortedObjetivos.map((o) => {
                    let ultimoEstado = t('flashboard-plus.list.card.last-status-time');
                    if (o.global.length) {
                        let globalFiltered = [...o.global[0].estado];
                        if (
                            globalFiltered[globalFiltered.length - 1].nombre ===
                            "no monitoreo"
                        ) {
                            globalFiltered.pop();
                        }
                        if (globalFiltered.length) {
                            const ultimoMonitoreo =
                                globalFiltered[globalFiltered.length - 1];
                            const horas = differenceInSeconds(
                                new Date(),
                                new Date(ultimoMonitoreo.fecha_desde)
                            );
                            if (horas < 21600) {
                                ultimoEstado = format(
                                    new Date(ultimoMonitoreo.fecha_desde),
                                    "yyyy-MM-dd HH:mm:ss"
                                );
                            }
                        }
                    } else {
                        ultimoEstado = t('flashboard-plus.list.card.no-tracking');
                    }
                    return { ...o, ultimoEstado };
                });
                setObjetivos(objetivosConEstado);
                setImagen(data.info.imagen);
                setTitutlo(data.info.titulo);
                setDateUpdate(
                    moment().locale("es").format("DD/MM/YYYY, HH:mm:ss")
                );
                if (autoPlaySlider) {
                    setIntervalTime(5000);
                }
            })
            .catch((error) => {
                console.log(error);
                setIsError(true);
            })
            .finally(() => {
                setLoading(false);
                setIsFirstRender(false);
            });
    };

    useEffect(() => {
        if (alerta) {
            playSound();
        }
    }, [alerta]);

    const handleSearchChange = (event) => {
        const value = event.target.value;
        setSearchTerm(value);
        setPage(0); // Resetear la página al cambiar el término de búsqueda
        const queryParamsParsed = queryString.parse(document.location.search);
        queryParamsParsed["etiquetas"] = tags.join("T");
        queryParamsParsed["estado"] = stateFilter;
        queryParamsParsed["filterType"] = filterTagsType;
        queryParamsParsed["search"] = value;
        queryParamsParsed["autoPagination"] = autoPlaySlider;
        window.history.replaceState(
            null,
            "Disponibilidad umbral",
            `${pathname}?${queryString.stringify(queryParamsParsed)}`
        );
        setPage(0);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
        if (autoPlaySlider) {
            setIntervalTime(5000);
        } else {
            setIntervalTime(null);
        }
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const filteredObjetivos = useMemo(() => {
        let filteredObjectectives = objetivos.filter(
            (item) => item.estado === stateFilter
        );
        /**Filtrar por estado */
        if (stateFilter === "Todos") filteredObjectectives = objetivos;
        if (stateFilter === "Activos")
            filteredObjectectives = objetivos.filter(
                (item) => item.estado !== "No monitoreo"
            );

        if (searchTerm !== "") {
            filteredObjectectives = filteredObjectectives.filter((item) => {
                return item.nombre
                    .toLowerCase()
                    .includes(searchTerm.toLowerCase());
            });
        }

        filteredObjectectives = filteredObjectectives.filter((item) =>
            item.nombre.toLowerCase().includes(searchTerm.toLowerCase())
        );

        if (tags.length === 0) return filteredObjectectives;
        filteredObjectectives = filteredObjectectives.filter((obj) => {
            if (!obj.etiquetas) return false;
            let tieneEtiquetas = true;
            if (filterTagsType === "AND") {
                for (const id of tags) {
                    if (!obj.etiquetas.find((et) => et.etiqueta_id === id)) {
                        tieneEtiquetas = false;
                        break;
                    }
                }
            } else {
                tieneEtiquetas = obj.etiquetas.filter((et) =>
                    tags.includes(et.etiqueta_id)
                ).length;
            }
            return tieneEtiquetas;
        });

        return filteredObjectectives;
    }, [tags, searchTerm, objetivos, stateFilter, filterTagsType]);

    const paginatedObjetivos = filteredObjetivos.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );

    const generateArray = (length) => {
        const result = [];
        let i = 4;
        while (i <= length) {
            result.push(i);
            i += 4;
        }
        if (result[result.length - 1] < length) {
            result.push(result[result.length - 1] + 4);
        }
        return result;
    };

    const rowsPerPageOptions = generateArray(objetivos.length);

    useInterval(() => {
        if (filteredObjetivos.length) {
            const pages = Math.ceil(filteredObjetivos.length / rowsPerPage) - 1;
            const newPage = page + 1;
            if (newPage <= pages) {
                setPage(newPage);
            } else {
                setPage(0);
            }
        }
    }, intervalTime);

    function updateEtiquetas(etiquetas, filterType) {
        const ids = etiquetas.map((etiqueta) => etiqueta.etiqueta_id);
        setTags(ids);
        setFilterTagsType(filterType);
        const queryParamsParsed = queryString.parse(document.location.search);
        queryParamsParsed["etiquetas"] = ids.join("T");
        queryParamsParsed["estado"] = stateFilter;
        queryParamsParsed["filterType"] = filterType;
        queryParamsParsed["search"] = searchTerm;
        queryParamsParsed["autoPagination"] = autoPlaySlider;
        window.history.replaceState(
            null,
            "Disponibilidad umbral",
            `${pathname}?${queryString.stringify(queryParamsParsed)}`
        );
        setPage(0);
    }

    function handleChangeStateFilter(e, item) {
        setStateFilter(item.value);
        const queryParamsParsed = queryString.parse(document.location.search);
        queryParamsParsed["etiquetas"] = tags.join("T");
        queryParamsParsed["estado"] = item.value;
        queryParamsParsed["filterType"] = filterTagsType;
        queryParamsParsed["search"] = searchTerm;
        queryParamsParsed["autoPagination"] = autoPlaySlider;
        window.history.replaceState(
            null,
            "Disponibilidad umbral",
            `${pathname}?${queryString.stringify(queryParamsParsed)}`
        );
        setPage(0);
    }

    function handleChangeAutoPagination(event) {
        const value = event.target.checked;
        setAutoPlaySlider(value);
        const queryParamsParsed = queryString.parse(document.location.search);
        queryParamsParsed["etiquetas"] = tags.join("T");
        queryParamsParsed["estado"] = stateFilter;
        queryParamsParsed["filterType"] = filterTagsType;
        queryParamsParsed["search"] = searchTerm;
        queryParamsParsed["autoPagination"] = value;
        window.history.replaceState(
            null,
            "Disponibilidad umbral",
            `${pathname}?${queryString.stringify(queryParamsParsed)}`
        );
        if (value) {
            setIntervalTime(5000);
        } else {
            setIntervalTime(null);
        }
    }

    if (isFirstRender && loading && !isError && !isErrorConfiguracionLimites) {
        return <Skeleton />;
    }

    if (isError || isErrorConfiguracionLimites) {
        return (
            <Box padding={3}>
                <Alert variant="filled" severity="error">
                    {t('flashboard.list.card.error-loading')}
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ padding: { xs: 0, sm: 0, md: 2, lg: 3, xl: 3 } }}>
            <Grid container>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    xl={6}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-start",
                        padding: "0.5rem",
                    }}
                >
                    <img
                        style={{ height: "50px", maxWidth: "315px" }}
                        src={imagen}
                        alt="logo cliente"
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    xl={6}
                    style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "flex-end",
                        padding: "0.5rem",
                    }}
                >
                    <Box
                        sx={{
                            display: {
                                xs: "none",
                                sm: "none",
                                md: "block",
                                lg: "block",
                                xl: "block",
                            },
                        }}
                    >
                        <img
                            style={{ height: "50px" }}
                            src={getImagenAtentus(window.innerWidth)}
                            alt="logo atentus"
                        />
                    </Box>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    xl={6}
                    style={{
                        display: "flex",
                        padding: "0.5rem",
                    }}
                >
                    <Typography variant="h3" style={{ color: "#6c757d" }}>
                        <strong>{titulo}</strong>
                    </Typography>
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={6}
                    lg={6}
                    xl={6}
                    style={{
                        color: "#6c757d",
                        display: "flex",
                        marginTop: 10,
                        justifyContent: "end",
                    }}
                >
                    <Grid
                        item
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            height: "24px",
                            padding: "0.5rem",
                            marginTop: "5px",
                        }}
                    >
                        <UpdateIcon />
                    </Grid>
                    <Grid
                        item
                        style={{
                            display: "flex",
                            justifyContent: "end",
                            height: "24px",
                            padding: "0.5rem",
                        }}
                    >
                        <strong>{t('text.last-update')}: {dateUpdate}</strong>
                        {loading && (
                            <Tooltip title={t('flashboard-plus.tooltip.refresh')}>
                                <CircularProgress
                                    color="primary"
                                    size={20}
                                    style={{ marginLeft: 10 }}
                                />
                            </Tooltip>
                        )}
                    </Grid>
                </Grid>
                {pathname === "/disponibilidadumbral" && (
                    <Grid
                        item
                        xs={12}
                        sm={12}
                        md={12}
                        lg={12}
                        xl={12}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            marginTop: 20,
                        }}
                    >
                        <Alert
                            severity="info"
                            style={{
                                width: "100%",
                                height: "2.5rem",
                            }}
                            icon={false}
                        >
                            <span
                                style={{
                                    display: "flex",
                                    alignItems: "center",
                                }}
                            >
                                {t('flashboard-plus.alert.message')}
                                <Link
                                    to={{
                                        pathname: "manageObjectives",
                                        query: { tab: 6 },
                                        search: getQueryParams(),
                                    }}
                                >
                                    <b>&nbsp;{t('flashboard-plus.alert.link.config')}</b>
                                </Link>
                                <PopoverFlashboard
                                    dataColores={
                                        dataColores.length
                                            ? dataColores.slice(
                                                  1,
                                                  dataColores.length - 1
                                              )
                                            : dataColores
                                    }
                                />
                            </span>
                        </Alert>
                    </Grid>
                )}
                <Grid item container xs={12}>
                    <FilterInputs
                        tags={tags}
                        filterTagsType={filterTagsType}
                        stateFilter={stateFilter}
                        autoPlaySlider={autoPlaySlider}
                        updateTags={updateEtiquetas}
                        changeStateFilter={handleChangeStateFilter}
                        searchTerm={searchTerm}
                        handleSearchChange={handleSearchChange}
                        changeAutoPlaySliderValue={handleChangeAutoPagination}
                    />
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    style={{ justifyContent: "flex-end" }}
                >
                    <TablePagination
                        component="div"
                        count={filteredObjetivos.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={rowsPerPageOptions}
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} ${t('text.of')} ${count}`
                        }
                        labelRowsPerPage={t('flashboard.list.pagination.elements-count')}
                    />
                </Grid>
                <Grid container>
                    {!objetivos.length && (
                        <Grid xs={12}>
                            <Box display="flex" justifyContent="center">
                                <Typography>{t('flashboard-plus.list.card.no-objectives')}</Typography>
                            </Box>
                        </Grid>
                    )}
                    {objetivos.length && !filteredObjetivos.length ? (
                        <Grid xs={12}>
                            <Box display="flex" justifyContent="center">
                                <Typography>
                                    {t('flashboard-plus.list.card.no-objectives-found')}
                                </Typography>
                            </Box>
                        </Grid>
                    ) : null}
                    {paginatedObjetivos.map((item) => {
                        if (
                            sessionStorage.getItem(item.id_objetivo) != null &&
                            item.color !== "#C2272D"
                        ) {
                            sessionStorage.removeItem(item.id_objetivo);
                        }
                        if (
                            sessionStorage.getItem(item.id_objetivo) == null &&
                            item.color === "#C2272D"
                        ) {
                            sessionStorage.setItem(
                                item.id_objetivo,
                                item.color
                            );
                            setAlerta(true);
                        }

                        if (item.umbral !== undefined) {
                            return (
                                <Grid
                                    key={item.id_objetivo}
                                    item
                                    container
                                    xs={12}
                                    sm={6}
                                    md={6}
                                    lg={4}
                                    xl={3}
                                    style={{ padding: "5px" }}
                                >
                                    <Grid
                                        item
                                        xs={12}
                                        sm={12}
                                        md={12}
                                        lg={12}
                                        xl={12}
                                        style={{
                                            background: "#fff",
                                            border: "4px solid",
                                            borderColor: item.color,
                                            padding: "10px",
                                            borderRadius: "0.5rem",
                                        }}
                                    >
                                        <Box
                                            style={{
                                                display: "flex",
                                                flexDirection: "column",
                                                justifyContent: "space-between",
                                                height: "100%",
                                            }}
                                        >
                                            <div>
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        alignItems: "center",
                                                        marginBottom: "0.5rem",
                                                        justifyContent:
                                                            "space-between",
                                                    }}
                                                >
                                                    <Box
                                                        style={{
                                                            display: "flex",
                                                            alignItems:
                                                                "center",
                                                        }}
                                                    >
                                                        <CircleIcon
                                                            style={{
                                                                borderRadius:
                                                                    "50%",
                                                                width: "0.8rem",
                                                                height:
                                                                    "0.8rem",
                                                                minWidth:
                                                                    "0.8rem",
                                                                minHeight:
                                                                    "0.8rem",
                                                                marginRight:
                                                                    "0.5rem",
                                                                color:
                                                                    item.estado ===
                                                                    "Umbral"
                                                                        ? "#41B900"
                                                                        : item.color,
                                                            }}
                                                        />
                                                        <Typography
                                                            style={{
                                                                fontSize:
                                                                    item.nombre
                                                                        .length >
                                                                    36
                                                                        ? "0.9rem"
                                                                        : "1rem",
                                                                fontWeight:
                                                                    "bold",
                                                            }}
                                                        >
                                                            {item.nombre}
                                                        </Typography>
                                                    </Box>
                                                    <Box display="flex">
                                                        <IconService
                                                            service={
                                                                item.servicio
                                                            }
                                                        />
                                                        <Tooltip title={t('flashboard-plus.list.card.threshold-tooltip')}>
                                                            <div
                                                                style={{
                                                                    textAlign:
                                                                        "left",
                                                                    background:
                                                                        "#e4e4e7",
                                                                    padding: 3,
                                                                    marginLeft:
                                                                        "0.5rem",
                                                                    display:
                                                                        "flex",
                                                                    alignItems:
                                                                        "center",
                                                                    borderRadius: 15,
                                                                }}
                                                            >
                                                                <TimerIcon
                                                                    style={{
                                                                        height:
                                                                            "1rem",
                                                                    }}
                                                                />
                                                                <Typography
                                                                    style={{
                                                                        fontSize:
                                                                            "0.9rem",
                                                                    }}
                                                                >
                                                                    {
                                                                        item.umbral
                                                                    }
                                                                    s
                                                                </Typography>
                                                            </div>
                                                        </Tooltip>
                                                    </Box>
                                                </div>
                                                <div>
                                                    {item.pasos !== undefined &&
                                                    item.pasos.length >
                                                        0 ? null : (
                                                        <div
                                                            style={{
                                                                textAlign:
                                                                    "center",
                                                                background:
                                                                    "#e4e4e7",
                                                                padding:
                                                                    "0.5rem",
                                                                marginBottom:
                                                                    "0.5rem",
                                                            }}
                                                        >
                                                            <span
                                                                style={{
                                                                    color:
                                                                        "#000000",
                                                                }}
                                                            >
                                                                <strong>
                                                                    {t('flashboard-plus.list.card.no-active-configuration')}
                                                                </strong>
                                                            </span>
                                                        </div>
                                                    )}
                                                    <div
                                                        style={{
                                                            textAlign: "left",
                                                            background:
                                                                "#e4e4e7",
                                                            padding: "0.5rem",
                                                            marginBottom:
                                                                "0.5rem",
                                                        }}
                                                    >
                                                        <span
                                                            style={{
                                                                color: "black",
                                                            }}
                                                        >
                                                            {t('flashboard-plus.list.card.last-status-update')}:{" "}
                                                            {item.ultimoEstado}
                                                        </span>
                                                    </div>
                                                </div>
                                                <Table
                                                    style={{ width: "100%" }}
                                                    size="small"
                                                >
                                                    {item.pasos?.length > 0 ? (
                                                        <TableHead>
                                                            <TableRow
                                                                style={{
                                                                    height:
                                                                        "2.2rem",
                                                                }}
                                                            >
                                                                <TableCell
                                                                    style={{
                                                                        padding:
                                                                            "0 3px 0 3px",
                                                                        textAlign:
                                                                            "left",
                                                                        fontWeight:
                                                                            "bold",
                                                                    }}
                                                                >
                                                                    {t('flashboard-plus.list.card.title.step')}
                                                                </TableCell>
                                                                <TableCell
                                                                    style={{
                                                                        padding:
                                                                            "0 3px 0 3px",
                                                                        textAlign:
                                                                            "right",
                                                                        fontWeight:
                                                                            "bold",
                                                                    }}
                                                                >
                                                                    {t('flashboard-plus.list.card.title.response-time')}
                                                                </TableCell>
                                                                <TableCell>
                                                                    {t('flashboard-plus.list.card.title.threshold')}
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                    ) : null}
                                                    <TableBody>
                                                        {item.pasos?.length > 0
                                                            ? item.pasos.map(
                                                                  (paso) => {
                                                                      return (
                                                                          <>
                                                                              {paso.visible && (
                                                                                  <Step
                                                                                      key={`${item.objetivo_id}-${paso.nombre_paso}`}
                                                                                      style={{
                                                                                          borderBottom:
                                                                                              "1px solid",
                                                                                      }}
                                                                                      title={
                                                                                          paso.nombre_paso
                                                                                      }
                                                                                      time={
                                                                                          paso.tiempo_respuesta
                                                                                      }
                                                                                      color={
                                                                                          paso.estado
                                                                                      }
                                                                                      umbral={
                                                                                          paso.umbral ||
                                                                                          "N/D"
                                                                                      }
                                                                                  />
                                                                              )}
                                                                          </>
                                                                      );
                                                                  }
                                                              )
                                                            : null}
                                                    </TableBody>
                                                </Table>
                                            </div>
                                            <div
                                                style={{
                                                    display: "flex",
                                                    flexDirection: "row",
                                                    marginTop: "0.5rem",
                                                    flexWrap: "wrap",
                                                }}
                                            >
                                                {item.etiquetas &&
                                                item.etiquetas.length ? (
                                                    <>
                                                        {item.etiquetas
                                                            .slice(0, 4)
                                                            .map((item) => (
                                                                <div
                                                                    key={
                                                                        item.etiqueta_id
                                                                    }
                                                                    style={{
                                                                        padding: 2,
                                                                    }}
                                                                >
                                                                    <ChipTag
                                                                        nombre={
                                                                            item.nombre
                                                                        }
                                                                        color={
                                                                            item.color
                                                                        }
                                                                    />
                                                                </div>
                                                            ))}
                                                        {item.etiquetas.length >
                                                        4 ? (
                                                            <ChipVerMas
                                                                etiquetas={
                                                                    item.etiquetas
                                                                }
                                                                size="small"
                                                                length={4}
                                                            />
                                                        ) : null}
                                                    </>
                                                ) : null}
                                            </div>
                                            <Box>
                                                {item.etiquetas.length > 0 &&
                                                    item.nodosDowntime.length >
                                                        0 && (
                                                        <Divider
                                                            style={{
                                                                marginTop: 5,
                                                                marginBottom: 5,
                                                                backgroundColor:
                                                                    "#ebebeb",
                                                            }}
                                                        />
                                                    )}
                                                <div
                                                    style={{
                                                        display: "flex",
                                                        flexDirection: "row",
                                                        marginTop: "0.5rem",
                                                        flexWrap: "wrap",
                                                    }}
                                                >
                                                    {item.nodosDowntime.map(
                                                        (nodo) => (
                                                            <div
                                                                key={
                                                                    nodo.nodo_id
                                                                }
                                                                style={{
                                                                    padding: 2,
                                                                }}
                                                            >
                                                                <span
                                                                    style={{
                                                                        fontSize:
                                                                            "12px",
                                                                        padding: 4,
                                                                        borderRadius: 3,
                                                                        backgroundColor:
                                                                            item.color,
                                                                        color: isColorLight(
                                                                            item.color
                                                                        )
                                                                            ? "#000000"
                                                                            : "#ffffff",
                                                                        display:
                                                                            "flex",
                                                                        alignItems:
                                                                            "center",
                                                                    }}
                                                                >
                                                                    <CloudOffIcon
                                                                        fontSize="small"
                                                                        style={{
                                                                            marginRight: 5,
                                                                        }}
                                                                    />
                                                                    {
                                                                        nodo.titulo
                                                                    }
                                                                </span>
                                                            </div>
                                                        )
                                                    )}
                                                </div>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            );
                        }
                        return null;
                    })}
                </Grid>
                <Grid
                    item
                    xs={12}
                    sm={12}
                    md={12}
                    lg={12}
                    xl={12}
                    style={{ justifyContent: "flex-end" }}
                >
                    <TablePagination
                        component="div"
                        count={filteredObjetivos.length}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={rowsPerPageOptions}
                        labelRowsPerPage={t('flashboard.list.pagination.elements-count')}
                    />
                </Grid>
            </Grid>
        </Box>
    );
}
